import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ArrowLeft, Upload } from "lucide-react";
import { Link } from "react-router-dom";


const mentorApplicationSchema = z.object({
  firstName: z.string().min(2, "First name must be at least 2 characters"),
  lastName: z.string().min(2, "Last name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  phone: z.string().min(10, "Please enter a valid phone number"),
  bio: z.string().min(50, "Please provide more details about your experience (at least 50 characters)"),
  specialty: z.string().min(10, "Please describe your specialty (at least 10 characters)"),
  expertise: z.string().min(1, "Please specify at least one area of expertise"),
  hourlyRate: z.number().min(1, "Please specify your desired hourly rate"),
  yearsExperience: z.number().min(1, "Please specify your years of experience").max(50, "Please enter a realistic number of years"),
  invoiceInfo: z.string().min(10, "Please provide your invoice information for payments"),
});

type MentorApplicationForm = z.infer<typeof mentorApplicationSchema>;

const MentorApplication = () => {
  // Set page title
  useEffect(() => {
    document.title = "Become a Mentor - Ascendry";
  }, []);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(null);

  const form = useForm<MentorApplicationForm>({
    resolver: zodResolver(mentorApplicationSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      bio: "",
      specialty: "",
      expertise: "", // Store as string during input
      hourlyRate: "" as any, // Allow empty string initially
      yearsExperience: "" as any, // Allow empty string initially
      invoiceInfo: "",
    },
  });


   const onSubmit = async (data: MentorApplicationForm) => {
     setIsSubmitting(true);
     try {
       console.log('Submitting mentor application data:', data);
       console.log('Data types:', Object.entries(data).map(([key, value]) => `${key}: ${typeof value} (${Array.isArray(value) ? 'array' : 'not array'})`));
       
        // Ensure expertise is an array - convert string to array here
        const expertiseArray = typeof data.expertise === 'string' ? 
          data.expertise.split(',').map(s => s.trim()).filter(s => s.length > 0) : 
          (Array.isArray(data.expertise) ? data.expertise : []);
       
       const submissionData = {
         first_name: data.firstName,
         last_name: data.lastName,
         email: data.email,
         phone: data.phone,
         bio: data.bio,
         specialty: data.specialty,
         expertise: expertiseArray,
         hourly_rate: (Number(data.hourlyRate) || 0) * 100, // Convert to cents, handle empty values
         years_experience: Number(data.yearsExperience) || 0,
         invoice_info: data.invoiceInfo,
       };
       
       console.log('Final submission data:', submissionData);
       
       // Store application in database
       const { data: appData, error: dbError } = await supabase
         .from('mentor_applications')
         .insert([submissionData])
         .select()
         .single();

       if (dbError) throw dbError;

      // Send detailed mentor application email
      const { error: emailError } = await supabase.functions.invoke('send-mentor-application', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          bio: data.bio,
          specialty: data.specialty,
          expertise: typeof data.expertise === 'string' ? data.expertise.split(',').map(s => s.trim()).filter(s => s.length > 0) : expertiseArray,
          hourlyRate: Number(data.hourlyRate) || 0,
          yearsExperience: Number(data.yearsExperience) || 0,
          invoiceInfo: data.invoiceInfo,
          submittedAt: new Date().toISOString(),
        },
      });

      if (emailError) {
        console.warn('Detailed email notification failed:', emailError);
      }

      // Also send admin notification using direct fetch (public endpoint)
      try {
        const response = await fetch(
          'https://llcojpvgertoimsualob.supabase.co/functions/v1/send-admin-notification',
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'mentor_application',
              userEmail: data.email,
              firstName: data.firstName,
              lastName: data.lastName,
              additionalInfo: {
                specialty: data.specialty,
                hourlyRate: Number(data.hourlyRate) || 0,
                yearsExperience: Number(data.yearsExperience) || 0
              }
            })
          }
        );
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          console.error('Admin notification failed:', errorData);
        } else {
          console.log('Admin notification sent successfully');
        }
      } catch (notifError) {
        console.error('Admin notification request failed:', notifError);
      }

      toast.success("Application submitted successfully! We'll review it and get back to you within 5-7 business days.");
      
      // Clear form
      form.reset();
      setApplicationId(null);
    } catch (error) {
      console.error('Error submitting application:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      
      // Check if it's a database error specifically
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.details) {
        console.error('Error details:', error.details);
      }
      if (error.hint) {
        console.error('Error hint:', error.hint);
      }
      
      toast.error(`Failed to submit application: ${error.message || 'Please try again.'}`);
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Header */}
      <header className="border-b border-border obsidian-glass">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-energy">Ascendry</h1>
          <Button variant="outline" asChild>
            <Link to="/">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold mb-4 text-glow">Become a Mentor</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-6">
              Join our platform of expert mentors and help shape the next generation of professionals. 
              We carefully review each application to maintain our high standards.
            </p>
            
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 max-w-3xl mx-auto">
              <h3 className="font-bold text-primary mb-3 text-center">🎓 Educational Mission Statement</h3>
              <p className="text-muted-foreground text-center mb-4">
                As an Ascendry mentor, you commit to providing legitimate educational services focused on skill development and knowledge transfer.
              </p>
              <div className="grid md:grid-cols-2 gap-6 text-sm">
                <div>
                  <h4 className="font-semibold text-primary mb-2">✅ What You Will Provide:</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• Expert knowledge and skills training</li>
                    <li>• Personalized guidance and feedback</li>
                    <li>• Industry insights and best practices</li>
                    <li>• Educational resources and advice</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-red-400 mb-2">❌ Prohibited Activities:</h4>
                  <ul className="space-y-1 text-muted-foreground">
                    <li>• Pay-to-play arrangements</li>
                    <li>• Guaranteed opportunities or placements</li>
                    <li>• Ghost production services</li>
                    <li>• Payola or paid promotional schemes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <Card className="obsidian-card">
            <CardHeader>
              <CardTitle className="text-2xl text-energy">Mentor Application</CardTitle>
              <p className="text-muted-foreground">
                Please fill out all fields thoroughly. We review each application to ensure we maintain 
                the highest quality standards for our mentors.
              </p>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your first name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name *</FormLabel>
                          <FormControl>
                            <Input placeholder="Your last name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address *</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="your.email@domain.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Phone Number *</FormLabel>
                          <FormControl>
                            <Input placeholder="+1 (555) 123-4567" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="specialty"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Specialty *</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g., Music Production, Audio Engineering, Sound Design" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="hourlyRate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Desired Hourly Rate (USD) *</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="150" 
                              value={field.value || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value === "" ? "" : Number(value));
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="yearsExperience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Years of Experience *</FormLabel>
                          <FormControl>
                            <Input 
                              type="number" 
                              placeholder="5" 
                              value={field.value || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value === "" ? "" : Number(value));
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                   <FormField
                     control={form.control}
                     name="expertise"
                     render={({ field }) => (
                       <FormItem>
                         <FormLabel>Areas of Expertise *</FormLabel>
                         <FormControl>
                           <Textarea
                             placeholder="e.g., Music Production, Sound Design, Audio Engineering, Mixing & Mastering, DJing, Music Business, Artist Development..."
                             value={field.value || ''}
                             onChange={(e) => {
                               // Store as string during typing to preserve spaces
                               field.onChange(e.target.value);
                             }}
                             className="min-h-[80px]"
                           />
                         </FormControl>
                         <p className="text-sm text-muted-foreground">
                           List your areas of expertise separated by commas (e.g., Music Production, Sound Design, Audio Engineering)
                         </p>
                         <FormMessage />
                       </FormItem>
                     )}
                  />

                  <FormField
                    control={form.control}
                    name="invoiceInfo"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invoice Information *</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Enter your invoicing details (name, address, tax ID, etc.)..." 
                            className="min-h-[100px]"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Professional Bio *</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Describe your professional background, key achievements, and relevant work experience..." 
                            className="min-h-[120px]"
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <Button type="submit" disabled={isSubmitting} className="flex-1 energy-button">
                      {isSubmitting ? "Submitting..." : "Submit Application"}
                    </Button>
                    <Button type="button" variant="outline" asChild className="flex-1">
                      <Link to="/">Cancel</Link>
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default MentorApplication;